import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import ChangePassword from '../ChangePassword/ChangePassword';
import ForgotPassword from '../ForgotPassword/ForgotPassword';

import closeIcon from '../../../../../Assets/images/close-icon.svg';
import nextIcon from '../../../../../Assets/images/next-icon.svg';

import { isValidEmail } from '../../../../../Utils/Validations/validateEmail';
import Loader from '../../../../../Context/Loader/Loader';
import { store } from '../../../../../Services/localStorageService';
import { FIELD_MAX_LENGTHS } from '../../Utils/Constants';
import { showErrorToast } from '../../../../../Context/Toaster/Toaster';
import { getFirstPermittedRoute } from '../../Services/NavigationList';

const basePath = process.env.REACT_APP_SERVER_BASE_URL;
const signInPath = process.env.REACT_APP_HS_SIGNIN_BASE_PATH;

const PLACEHOLDERS = {
    email: 'e.g. john@company.com',
    password: 'Enter your password',
};

const HINTS = {
    email: 'Enter your email address',
    password: 'Enter your password',
};

const SignInForm = ({ onClose, onOpenSignup }) => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [userId, setUserId] = useState(null);
    const [touched, setTouched] = useState({});

    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (touched[name]) validateField(name, value);
        else setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateField = (name, value) => {
        const val = value !== undefined ? value : formData[name];
        let fieldError = '';
        if (name === 'email') {
            fieldError = !val.trim() ? 'Email is required' : !isValidEmail(val) ? 'Enter a valid email' : '';
        } else if (name === 'password') {
            fieldError = !val ? 'Password is required' : '';
        }
        setErrors((prev) => ({ ...prev, [name]: fieldError }));
    };

    const handleBlur = (name) => {
        setTouched((prev) => ({ ...prev, [name]: true }));
        validateField(name);
    };

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    const validate = () => {
        const newErrors = {};
        const { email, password } = formData;

        if (!email || !email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!isValidEmail(email)) {
            newErrors.email = 'Enter a valid email';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        try {
            const response = await axios.post(`${basePath}${signInPath}/login`, formData);

            if (response.data.status === 'SUCCESS') {
                const {
                    access_token,
                    refresh_token,
                    change_password_required,
                    data,
                } = response.data;

                const accessDecoded = jwtDecode(access_token);
                store('accessToken', access_token);
                store('accessTokenExpiry', accessDecoded.exp * 1000);

                const refreshDecoded = jwtDecode(refresh_token);
                store('refreshToken', refresh_token);
                store('refreshTokenExpiry', refreshDecoded.exp * 1000);

                if (change_password_required) {
                    setUserId(data?.user?.id);
                    setShowChangePasswordModal(true);
                } else {
                    const firstPath = getFirstPermittedRoute();
                    if (firstPath) {
                        navigate(firstPath);
                    } else {
                        navigate("/access-denied")
                    }
                }
            } else {
                showErrorToast(response.data.message || 'Login failed');
            }
        } catch (err) {
            const msg = err.response ? err.response.data.message : err.message;
            console.error(msg);
            showErrorToast(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {!showChangePasswordModal && !showForgot && (
                <div className="form-cvr">
                    <div className="h-form signin-form">
                        <button className="close-button" type="button" onClick={onClose}>
                            <img src={closeIcon} alt="Close" />
                        </button>
                        <div className="form-heading">
                            <h1>Welcome to HaulSync</h1>
                            <h2>Sign In to</h2>
                            <p>HaulSync</p>
                        </div>
                        <div className="signin-fields">
                            <div className="hf-split c-name">
                                <label className="fi-label">Email</label>
                                <input
                                    type="text"
                                    className={`common-input ${errors.email ? 'error' : ''} ${touched.email && !errors.email && formData.email ? 'valid' : ''}`}
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('email')}
                                    placeholder={PLACEHOLDERS.email}
                                    maxLength={FIELD_MAX_LENGTHS.email}
                                />
                                {!touched.email
                                    ? <span className="field-feedback field-hint">{HINTS.email}</span>
                                    : touched.email && !errors.email && formData.email
                                        ? <span className="field-feedback field-success">Looks good ✓</span>
                                        : <span className="field-feedback field-error">{errors.email}</span>
                                }
                            </div>
                            <div className="hf-split c-name">
                                <label className="fi-label">Password</label>
                                <div className="password-box" style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className={`common-input ${errors.password ? 'error' : ''} ${touched.password && !errors.password && formData.password ? 'valid' : ''}`}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('password')}
                                        placeholder={PLACEHOLDERS.password}
                                        maxLength={FIELD_MAX_LENGTHS.password}
                                    />
                                    <i
                                        className={`fa field-icon ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                                        onClick={togglePasswordVisibility}
                                        style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', cursor: 'pointer' }}
                                    />
                                </div>
                                {!touched.password
                                    ? <span className="field-feedback field-hint">{HINTS.password}</span>
                                    : touched.password && !errors.password && formData.password
                                        ? <span className="field-feedback field-success">Looks good ✓</span>
                                        : <span className="field-feedback field-error">{errors.password}</span>
                                }
                            </div>
                        </div>
                        <button
                            type="button"
                            className="forgot-link"
                            onClick={() => setShowForgot(true)}
                        >
                            Forgot Password?
                        </button>
                        <button
                            type="button"
                            className="firstNext next h-btn"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <Loader /> : <>Sign In <img src={nextIcon} alt="Next" /></>}
                        </button>
                    </div>
                </div>
            )}

            {showChangePasswordModal && (
                <ChangePassword userId={userId} />
            )}

            {showForgot && (
                <ForgotPassword
                    onClose={() => setShowForgot(false)}
                    onConfirmClose={() => {
                        setShowForgot(false);
                        onClose();
                    }}
                />
            )}
        </>
    );
};

export default SignInForm;
